import type { FC, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'

import { useId } from 'react'
import { CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'

const controlClass = 'w-full rounded-xl border border-input bg-white px-4 text-base outline-none transition placeholder:text-muted-foreground focus:border-foreground focus:ring-1 focus:ring-foreground aria-invalid:border-destructive'

interface FieldShellProps {
    id: string
    label: string
    optional?: boolean
    className?: string
    children: ReactNode
}

const FieldShell: FC<FieldShellProps> = ({ id, label, optional, className, children }): ReactNode => (
    <div className={className}>
        <label htmlFor={id} className='mb-1.5 block text-sm font-medium'>
            {label}
            {optional && <span className='font-normal text-muted-foreground'> (optional)</span>}
        </label>
        {children}
    </div>
)

type TextFieldProps = InputHTMLAttributes<HTMLInputElement> & { label: string, optional?: boolean, wrapperClassName?: string }

export const TextField: FC<TextFieldProps> = ({ label, optional, wrapperClassName, className, ...props }): ReactNode => {
    const id = useId()

    return (
        <FieldShell id={id} label={label} optional={optional} className={wrapperClassName}>
            <input id={id} className={cn(controlClass, 'h-12', className)} {...props} />
        </FieldShell>
    )
}

type TextAreaFieldProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string, optional?: boolean, wrapperClassName?: string }

export const TextAreaField: FC<TextAreaFieldProps> = ({ label, optional, wrapperClassName, className, ...props }): ReactNode => {
    const id = useId()

    return (
        <FieldShell id={id} label={label} optional={optional} className={wrapperClassName}>
            <textarea id={id} rows={4} className={cn(controlClass, 'resize-y py-3', className)} {...props} />
        </FieldShell>
    )
}

type SelectFieldProps = SelectHTMLAttributes<HTMLSelectElement> & { label: string, optional?: boolean, wrapperClassName?: string, children: ReactNode }

export const SelectField: FC<SelectFieldProps> = ({ label, optional, wrapperClassName, className, children, ...props }): ReactNode => {
    const id = useId()

    return (
        <FieldShell id={id} label={label} optional={optional} className={wrapperClassName}>
            <select id={id} className={cn(controlClass, 'h-12 appearance-auto', className)} {...props}>
                {children}
            </select>
        </FieldShell>
    )
}

/* Hidden from people, left in place for spam bots to fill in. */
export const HoneypotField: FC<{ value: string, onChange: (value: string) => void }> = ({ value, onChange }): ReactNode => (
    <div className='absolute -left-[9999px] h-px w-px overflow-hidden' aria-hidden='true'>
        <label>
            Website
            <input type='text' tabIndex={-1} autoComplete='off' value={value} onChange={event => onChange(event.target.value)} />
        </label>
    </div>
)

export const FormSuccess: FC<{ title: string, children: ReactNode, onReset?: () => void, resetLabel?: string }> = ({ title, children, onReset, resetLabel = 'Send another' }): ReactNode => (
    <div className='flex flex-col items-center py-6 text-center' role='status'>
        <span className='flex size-14 items-center justify-center rounded-full bg-brand-soft text-primary'>
            <CheckCircle2 className='size-7' aria-hidden='true' />
        </span>
        <p className='mt-4 text-lg font-semibold'>{title}</p>
        <div className='mt-2 max-w-sm text-sm text-muted-foreground'>{children}</div>
        {onReset && (
            <button type='button' onClick={onReset} className='mt-5 rounded-lg px-3 py-1.5 text-sm font-semibold underline underline-offset-4 hover:bg-muted'>
                {resetLabel}
            </button>
        )}
    </div>
)

export const FormError: FC<{ message: string | null }> = ({ message }): ReactNode => (
    message
        ? <p className='rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive' role='alert'>{message}</p>
        : null
)
